import { useState, useRef, useEffect } from 'react';
import { PaletaDeComandos } from '@/components/datos/PaletaDeComandos';
import { Menu, Moon, Sun, LogOut, ChevronDown, HelpCircle, Compass, KeyRound, Store } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMenu } from '@/hooks/useMenu';
import { useUiStore } from '@/store/ui.store';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { roleLabel } from '@/lib/badges';
import { startTour } from '@/lib/tour';
import { WorkspaceSelector } from './WorkspaceSelector';
import { CambiarClaveModal } from './CambiarClaveModal';
import { BotonDePausa } from './Pausas';
import { Ventanita } from './Ventanita';
import { MenuVerComo } from './VerComo';
import { viendoComo, volverAMiCuenta } from '@/lib/ver-como';
import { useEsEscritorio } from '@/hooks/useEsEscritorio';

export function Header() {
  const { user, logout } = useAuth();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const abrirMenuMovil = useUiStore((s) => s.abrirMenuMovil);
  const menuMovilAbierto = useUiStore((s) => s.menuMovilAbierto);
  const escritorio = useEsEscritorio();
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  // La misma consulta del menú lateral (va en caché): no es otra petición.
  const sitio = useMenu().datos?.sitio;
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [claveOpen, setClaveOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setHelpOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    // 21-oct, macOS 27: barra de herramientas de 52 (56 en el teléfono), fondo de la ventana y cápsulas de cristal.
    <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-surface-border bg-surface px-3 lateral:h-[52px] lateral:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {/* En escritorio pliega y despliega el lateral. En el teléfono el lateral no cabe y
            está escondido: ahí abre el menú del teléfono, con las mismas pantallas. Antes
            pegaba el lateral escondido y en el teléfono no hacía nada. */}
        <button
          onClick={escritorio ? toggleSidebar : abrirMenuMovil}
          className="mac-capsula w-11 lateral:w-9"
          aria-label="Menú"
          aria-expanded={escritorio ? sidebarOpen : menuMovilAbierto}
          aria-controls={escritorio ? undefined : 'menu-movil'}
        >
          <Menu className="h-4 w-4" strokeWidth={1.75} />
        </button>
        {/* Negocio activo. Arriba a la izquierda porque es el contexto de todo
            lo demás: lo que se ve en el panel es lo de ESTE negocio. Se pinta
            solo si el servidor dice que corresponde (ver WorkspaceSelector). */}
        <WorkspaceSelector />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/* La paleta de comandos vive ACÁ, no en una fila propia arriba de cada
            pantalla: esa fila ocupaba alto en las siete pantallas para dar un
            solo botón. Busca y además ejecuta ("Nueva venta", "Agregar gasto"). */}
        <PaletaDeComandos />

        {/* La página y el panel van unidos: desde cualquier pantalla el equipo
            vuelve a la página pública. En la otra dirección, la página trae
            «Equipo» en el pie. Solo sale si el negocio tiene página. */}
        {sitio && (
          <a
            href={sitio}
            target="_blank"
            rel="noreferrer"
            aria-label="Ver la tienda"
            className="mac-capsula px-3 text-sm"
          >
            <Store className="h-4 w-4" strokeWidth={1.75} />
            <span className="hidden lg:inline">Ver la tienda</span>
          </a>
        )}

        {/* Reunión del 12-oct: la pausa de la asesora y la ventanita para pedir cambios. */}
        <BotonDePausa />
        <Ventanita />

        {/* Menú de Ayuda: relanzar el tour + recordatorio de los "?" por pantalla. */}
        <div className="relative" ref={helpRef} data-tour="help">
          <button
            onClick={() => setHelpOpen((v) => !v)}
            className="mac-capsula w-11 lateral:w-9"
            aria-label="Ayuda"
          >
            <HelpCircle className="h-4 w-4" strokeWidth={1.75} />
          </button>
          {helpOpen && (
            <div className="mac-menu absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-1.5rem)]">
              <button
                onClick={() => {
                  setHelpOpen(false);
                  startTour();
                }}
                className="mac-menu-fila"
              >
                <Compass className="h-4 w-4" />
                Ver el tour de bienvenida
              </button>
              <p className="px-[9px] py-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Cada pantalla tiene un botón “?” junto al título que explica qué hacer ahí.
              </p>
            </div>
          )}
        </div>

        <button
          onClick={toggleTheme}
          className="mac-capsula w-11 lateral:w-9"
          aria-label="Cambiar tema"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
        </button>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="mac-capsula pl-1 pr-1 sm:pr-3"
            aria-label="Mi cuenta"
          >
            <Avatar name={user?.name ?? user?.email} size="sm" />
            <div className="hidden text-left sm:block">
              <p className="max-w-[160px] truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {user?.name ?? user?.email}
              </p>
            </div>
            <ChevronDown className="hidden h-3.5 w-3.5 text-slate-500 sm:block" />
          </button>

          {menuOpen && (
            <div className="mac-menu absolute right-0 z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)]">
              <div className="mb-1 border-b border-surface-border px-[9px] pb-2 pt-1.5">
                <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                  {user?.name ?? '—'}
                </p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
                {user?.role && (
                  <Badge variant="brand" className="mt-1.5">
                    {roleLabel[user.role] ?? user.role}
                  </Badge>
                )}
              </div>
              <MenuVerComo />
              {!viendoComo() && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setClaveOpen(true);
                  }}
                  className="mac-menu-fila"
                >
                  <KeyRound className="h-4 w-4" />
                  Cambiar contraseña
                </button>
              )}
              <button
                // Mirando otra cuenta, «cerrar sesión» vuelve a la de la administración.
                onClick={() => (viendoComo() ? volverAMiCuenta() : void logout())}
                className="mac-menu-fila"
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
      {claveOpen && <CambiarClaveModal onClose={() => setClaveOpen(false)} />}
    </header>
  );
}
